import { Component, EventEmitter, Inject, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslocoLocaleWrapperService } from 'app/fuse-template/core/transloco/translocoLocale.service';
import { formatDate } from 'app/helpers/formatDate';
import { DivisionOfRulingAgeRelatedAcquisitionRes, DivisionOfRulingEqualAcquisitionRes, ExposedDivisionOfRulingDetailsRes, ExposedListKriaDivisionOfRulingsRes } from 'app/interfaces/responses/Exposed/exposedDivisionOfRulingDetailsRes';
import { ExposedListDivisionOfRulingsLSRARes } from 'app/interfaces/responses/Exposed/exposedListDivisionOfRulingsLSRARes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { PensionService } from 'app/services/api/pension/pension.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { isNaN } from 'lodash';
import { DialogGeneral } from '../../DialogGeneral';


export interface DialogPensionDivisionOfRulingsData {
    ruling: ExposedListDivisionOfRulingsLSRARes;
}

@Component({
    selector: 'dialog-pension-division-of-rulings',
    templateUrl: 'dialog-pension-division-of-rulings.html',
    styleUrls: ['dialog-pension-division-of-rulings.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DialogPensionDivisionOfRulings extends DialogGeneral implements OnInit {
    //Form
    form: FormGroup;
    formEnabled: boolean;
    divisionOfRulingConfirmed = false;
    isLoading = true;
    isSaving = false;

    // Output
    @Output() updateListDivisionOfRulingsLSRARes = new EventEmitter<any>();

    //Table
    divisionOfRulingDetailsRes: ExposedDivisionOfRulingDetailsRes;
    pensionTableColumns: string[] = [];
    ageRelatedAcquisitionTableColumns: string[] = [];
    equalAcquisitionTableColumn: string[] = [];
    dataSourceAgeRelatedAcquisition = new MatTableDataSource<DivisionOfRulingAgeRelatedAcquisitionRes>();
    dataSourcePension = new MatTableDataSource<ExposedListKriaDivisionOfRulingsRes>();
    dataSourceEqualAcquisition = new MatTableDataSource<DivisionOfRulingEqualAcquisitionRes>();
    relatedRulings: string = "";
    invalidRelatedRulings: string = "";
    relatedParentRuling: string = "";
    invalidrelatedParentRuling: string = "";

    //#region Init
    constructor(
        public matDialogRef: MatDialogRef<DialogPensionDivisionOfRulings>,
        private formBuilder: UntypedFormBuilder,
        public dialogRef: MatDialogRef<DialogPensionDivisionOfRulings>,
        public dialog: MatDialog,
        private translationService: TranslationService,
        public lsrauthService: LsrauthService,
        private loaderService: LoaderService,
        public pensionService: PensionService,
        private localeService: TranslocoLocaleWrapperService,
        @Inject(MAT_DIALOG_DATA) public data: DialogPensionDivisionOfRulingsData
    ) {

        super(dialogRef);
        this.fetchRulingDetails();
        this.initForm();
    }

    ngOnInit(): void {
        this.form.controls.fundAgeRelated.valueChanges.subscribe((value) => {
            const valueString = value.replace(',', '.');
            const fundEqual = this.form.get('fundEqual').value.toString().replace(',', '.');

            if (!this.divisionOfRulingConfirmed) {
                if (parseFloat(valueString) !== this.divisionOfRulingDetailsRes.summary.ageRelatedAcquisitionRatio) {
                    this.form.markAsDirty();
                    this.setCommentValidators();
                    this.formatSummaryValue('total', Number(valueString) + Number(fundEqual), 2, 2);
                }
                else {
                    this.removeCommentValidators();
                    this.form.markAsPristine();
                }
            }
            else {
                this.form.markAsDirty();
                this.formatSummaryValue('total', Number(valueString) + Number(fundEqual), 2, 2);
            }
        });

        this.form.controls.fundEqual.valueChanges.subscribe((value) => {
            const valueString = value.replace(',', '.');
            const fundAgeRelated = this.form.get('fundAgeRelated').value.toString().replace(',', '.');

            if (!this.divisionOfRulingConfirmed) {
                if (parseFloat(valueString) !== this.divisionOfRulingDetailsRes.summary.equalAcquisitionRatio) {
                    this.form.markAsDirty();
                    this.setCommentValidators();
                    this.formatSummaryValue('total', Number(valueString) + Number(fundAgeRelated), 2, 2);
                }
                else {
                    this.removeCommentValidators();
                    this.form.markAsPristine();
                }
            }
            else {
                this.form.markAsDirty();
                this.formatSummaryValue('total', Number(valueString) + Number(fundAgeRelated), 2, 2);
            }
        });

        this.form.controls.aRatio.valueChanges.subscribe((value) => {
            const valueString = value.replace(',', '.');

            if (!this.divisionOfRulingConfirmed) {
                if (parseFloat(valueString) !== this.divisionOfRulingDetailsRes.summary.aBenefitsTransactionTypeRatio) {
                    this.form.markAsDirty();
                    this.setCommentValidators();
                    this.formatSummaryValue('fundAgeRelated', Number(valueString), 2, 2);
                    this.calculateTotal();
                }
                else {
                    this.removeCommentValidators();
                    this.form.markAsPristine();
                }
            }
            else {
                this.form.markAsDirty();
                this.formatSummaryValue('fundAgeRelated', Number(valueString), 2, 2);
                this.calculateTotal();
            }
        });

        this.form.controls.jRatio.valueChanges.subscribe((value) => {
            const valueString = value.replace(',', '.');
            const lRatio = this.form.get('lRatio').value.toString().replace(',', '.');
            const sRatio = this.form.get('sRatio').value.toString().replace(',', '.');

            if (!this.divisionOfRulingConfirmed) {
                if (parseFloat(valueString) !== this.divisionOfRulingDetailsRes.summary.jBenefitsTransactionTypeRatio) {
                    this.form.markAsDirty();
                    this.setCommentValidators();
                    this.formatSummaryValue('fundEqual', Number(valueString) + Number(lRatio) + Number(sRatio), 2, 2);
                    this.calculateTotal();
                }
                else {
                    this.removeCommentValidators();
                    this.form.markAsPristine();
                }
            }
            else {
                this.form.markAsDirty();
                this.formatSummaryValue('fundEqual', Number(valueString) + Number(lRatio) + Number(sRatio), 2, 2);
                this.calculateTotal();
            }
        });

        this.form.controls.lRatio.valueChanges.subscribe((value) => {
            const valueString = value.replace(',', '.');
            const jRatio = this.form.get('jRatio').value.toString().replace(',', '.');
            const sRatio = this.form.get('sRatio').value.toString().replace(',', '.');

            if (!this.divisionOfRulingConfirmed) {
                if (parseFloat(valueString) !== this.divisionOfRulingDetailsRes.summary.lBenefitsTransactionTypeRatio) {
                    this.form.markAsDirty();
                    this.setCommentValidators();
                    this.formatSummaryValue('fundEqual', Number(valueString) + Number(jRatio) + Number(sRatio), 2, 2);
                    this.calculateTotal();
                }
                else {
                    this.removeCommentValidators();
                    this.form.markAsPristine();
                }
            }
            else {
                this.form.markAsDirty();
                this.formatSummaryValue('fundEqual', Number(valueString) + Number(jRatio) + Number(sRatio), 2, 2);
                this.calculateTotal();
            }
        });

        this.form.controls.sRatio.valueChanges.subscribe((value) => {
            const valueString = value.replace(',', '.');
            const jRatio = this.form.get('jRatio').value.toString().replace(',', '.');
            const lRatio = this.form.get('lRatio').value.toString().replace(',', '.');

            if (!this.divisionOfRulingConfirmed) {
                if (parseFloat(valueString) !== this.divisionOfRulingDetailsRes.summary.sBenefitsTransactionTypeRatio) {
                    this.form.markAsDirty();
                    this.setCommentValidators();
                    this.formatSummaryValue('fundEqual', Number(valueString) + Number(jRatio) + Number(lRatio), 2, 2);
                    this.calculateTotal();
                }
                else {
                    this.removeCommentValidators();
                    this.form.markAsPristine();
                }
            }
            else {
                this.form.markAsDirty();
                this.formatSummaryValue('fundEqual', Number(valueString) + Number(jRatio) + Number(lRatio), 2, 2);
                this.calculateTotal();
            }
        });
    }

    initForm(): void {
        this.form = this.formBuilder.group({
            fundAgeRelated: new FormControl(''),
            fundEqual: new FormControl(''),
            total: new FormControl(''),
            comment: new FormControl(''),
            aRatio: new FormControl(''),
            jRatio: new FormControl(''),
            lRatio: new FormControl(''),
            sRatio: new FormControl('')
        });
        this.form.markAsPristine();
        this.formEnabled = true;
    }

    fetchRulingDetails(): void {
        this.isLoading = true;
        this.pensionService.getDivisionOfRulingDetails({
            rulingNumber: this.data.ruling.rulingNumber,
            applicationNumber: this.data.ruling.applicationNumber
        }).subscribe(res => {
            this.divisionOfRulingDetailsRes = res;
            this.divisionOfRulingConfirmed = res.summary.isConfirmed;
            if (res.relatedRulingsSummary != null) {
                this.relatedRulings = res.relatedRulingsSummary == null ? "" : res.relatedRulingsSummary.relatedRulings.join(', ');
                this.invalidRelatedRulings = res.relatedRulingsSummary.invalidRelatedRulings.join(', ');
                this.relatedParentRuling = res.relatedRulingsSummary.relatedParentRuling.toString();
                this.invalidrelatedParentRuling = res.relatedRulingsSummary.invalidRelatedParentRuling != null ? res.relatedRulingsSummary.invalidRelatedParentRuling.toString() : '';
            }
            if (res.divisionOfRulingsRes != null && res.divisionOfRulingsRes.length > 0) {
                // Tables
                this.initTableColumns();
                this.dataSourcePension = new MatTableDataSource(res.divisionOfRulingsRes);
                this.dataSourceAgeRelatedAcquisition = new MatTableDataSource(res.ageRelatedAcquisition);
                this.dataSourceEqualAcquisition = new MatTableDataSource(this.getDataSourceEqulAcquisition(res.equalAcquisition));

                // Summary section
                this.formatSummaryValue('fundAgeRelated', res.summary.ageRelatedAcquisitionRatio, 2, 2);
                this.formatSummaryValue('fundEqual', res.summary.equalAcquisitionRatio, 2, 2);
                this.formatSummaryValue('total', res.summary.ageRelatedAcquisitionRatio + res.summary.equalAcquisitionRatio, 2, 2);
                this.formatSummaryValue('aRatio', res.summary.aBenefitsTransactionTypeRatio, 3, 3);
                this.formatSummaryValue('jRatio', res.summary.jBenefitsTransactionTypeRatio, 3, 3);
                this.formatSummaryValue('lRatio', res.summary.lBenefitsTransactionTypeRatio, 3, 3);
                this.formatSummaryValue('sRatio', res.summary.sBenefitsTransactionTypeRatio, 3, 3);

                this.setValidators();
                if (this.divisionOfRulingConfirmed) {
                    this.setCommentValidators();
                }
            }
            // In rare cases no calculation is found for the ruling from Kria, e.g when application number is 0. Fore thease rulings only summary section is shown.
            else if (res.divisionOfRulingsRes == null && this.divisionOfRulingConfirmed) {
                // Summary section
                this.formatSummaryValue('fundAgeRelated', res.summary.ageRelatedAcquisitionRatio, 2, 2);
                this.formatSummaryValue('fundEqual', res.summary.equalAcquisitionRatio, 2, 2);
                this.formatSummaryValue('total', res.summary.ageRelatedAcquisitionRatio + res.summary.equalAcquisitionRatio, 2, 2);
                this.formatSummaryValue('aRatio', res.summary.aBenefitsTransactionTypeRatio, 3, 3);
                this.formatSummaryValue('jRatio', res.summary.jBenefitsTransactionTypeRatio, 3, 3);
                this.formatSummaryValue('lRatio', res.summary.lBenefitsTransactionTypeRatio, 3, 3);
                this.formatSummaryValue('sRatio', res.summary.sBenefitsTransactionTypeRatio, 3, 3);

                this.setValidators();
                if (this.divisionOfRulingConfirmed) {
                    this.setCommentValidators();
                }
            }
            this.isLoading = false;
        });
    }

    // The data source for equal acquisition is processed to allow merged rows for the total row for J and L row.
    getDataSourceEqulAcquisition(equalAcquisitionRes): DivisionOfRulingEqualAcquisitionRes[] {
        const processedData = [];
        let skipNext = false;

        for (let i = 0; i < equalAcquisitionRes.length; i++) {
            const current = equalAcquisitionRes[i];
            if (skipNext) {
                processedData.push({ ...current, showJ: false });
                skipNext = false;
                continue;
            }

            if (current.benefitsTransactionType === 'J' && equalAcquisitionRes[i + 1]?.benefitsTransactionType === 'L' &&
                (current.finalResult > 0 && equalAcquisitionRes[i + i].finalResult > 0)) {
                processedData.push({ ...current, rowspan: 2, showJ: true });
                skipNext = true;
            } else {
                processedData.push({ ...current, showJ: true });
            }
        }
        return processedData;
    }

    //#endregion

    //#region Save

    canSave(): boolean {
        if (!this.divisionOfRulingConfirmed) {
            return this.form.valid && !this.isSaving;
        }
        return this.form.dirty && this.form.valid && !this.isSaving;
    }

    save(): void {
        if (this.isSaving) {
            return;
        }
        this.isSaving = true;
        this.pensionService.updateDivisionOfRulings({
            rulingNumber: this.data.ruling.rulingNumber,
            ssn: this.divisionOfRulingDetailsRes.divisionOfRulingsRes[0].fundPartnerSSN,
            atRatio: parseFloat(this.form.get('fundAgeRelated').value.replace(',', '.')),
            jaRatio: parseFloat(this.form.get('fundEqual').value.replace(',', '.')),
            aRatio: parseFloat(this.form.get('aRatio').value.replace(',', '.')),
            jRatio: parseFloat(this.form.get('jRatio').value.replace(',', '.')),
            lRatio: parseFloat(this.form.get('lRatio').value.replace(',', '.')),
            sRatio: parseFloat(this.form.get('sRatio').value.replace(',', '.')),
            comment: this.form.get('comment').value,
            relatedRulingsSummary: this.divisionOfRulingDetailsRes.relatedRulingsSummary,
            userIdEdit: null
        }).subscribe({
            next: () => {
                this.fetchRulingDetails();
                this.form.get('comment').setValue('');
                this.isSaving = false;

                // The changes of the ruling data are also updated in the overview list for the values listed.
                this.data.ruling.atRatio = parseFloat(this.form.get('fundAgeRelated').value.replace(',', '.'));
                this.data.ruling.jaRatio = (this.form.get('fundEqual').value.replace(',', '.'));
                this.data.ruling.divisionOfRulingStatus = this.data.ruling.divisionOfRulingStatus == 'Óstaðfest' ? 'Staðfest' : this.data.ruling.divisionOfRulingStatus;
                this.data.ruling.divisionOfRulingChanged = this.data.ruling.divisionOfRulingStatus == 'Staðfest' ? true : this.data.ruling.divisionOfRulingChanged;
                this.updateListDivisionOfRulingsLSRARes.emit(this.data);
            }
        });
    }

    //#endregion

    //#region Table funcionts

    initTableColumns(): any {
        this.pensionTableColumns = ['fund', 'rulingNumber', 'fundPartnerSSN', 'benefitsTransactionType'];

        if (this.isPensionType()) {
            this.pensionTableColumns.push('benefitPension', 'postponementAT', 'benefitPension2', 'postponementJA');
        }
        else if (this.isSpouseType()) {
            this.pensionTableColumns.push('benefitSpouse', 'postponementAT', 'benefitSpouse2', 'postponementJA');
        }
        else if (this.isDisablityType()) {
            this.pensionTableColumns.push('benefitDisablity', 'postponementAT', 'benefitDisablity2', 'postponementJA');
        }

        this.pensionTableColumns.push('benefitsFuture');

        this.ageRelatedAcquisitionTableColumns = ['benefitsTransactionType', 'ageRelatedAcquisitionTotalAmount', 'ageRelatedAcquisitionRatio'];
        this.equalAcquisitionTableColumn = ['benefitsTransactionType', 'ageRelatedAcquisitionTotal', 'equalAcquisitionTotal',
            'leadningPensionAcquisitionType', 'finalResult', 'ratio'];
    }

    //#endregion

    //#region Helper functions

    setValidators(): void {
        this.form.controls.fundAgeRelated.setValidators([Validators.required, Validators.min(0.00), Validators.max(100.00)]);
        this.form.controls.fundAgeRelated.updateValueAndValidity();

        this.form.controls.fundEqual.setValidators([Validators.required, Validators.min(0.00), Validators.max(100.00)]);
        this.form.controls.fundEqual.updateValueAndValidity();

        this.form.controls.total.setValidators([Validators.required, Validators.min(100.00), Validators.max(100.00)]);
        this.form.controls.total.updateValueAndValidity();

        this.form.controls.aRatio.setValidators([Validators.required, Validators.min(0.000), Validators.max(100.000)]);
        this.form.controls.aRatio.updateValueAndValidity();

        this.form.controls.jRatio.setValidators([Validators.required, Validators.min(0.000), Validators.max(100.000)]);
        this.form.controls.jRatio.updateValueAndValidity();

        this.form.controls.lRatio.setValidators([Validators.required, Validators.min(0.000), Validators.max(100.000)]);
        this.form.controls.lRatio.updateValueAndValidity();

        this.form.controls.sRatio.setValidators([Validators.required, Validators.min(0.000), Validators.max(100.000)]);
        this.form.controls.sRatio.updateValueAndValidity();
    }

    setCommentValidators(): void {
        this.form.controls.comment.setValidators(Validators.required);
        this.form.controls.comment.updateValueAndValidity();
    }

    removeCommentValidators(): void {
        this.form.controls.comment.clearValidators();
        this.form.controls.comment.updateValueAndValidity();
    }

    calculateTotal() {
        this.formatSummaryValue('total',
            Number(this.form.get('fundAgeRelated').value.toString().replace(',', '.')) +
            Number(this.form.get('fundEqual').value.toString().replace(',', '.')), 2, 2);
    }

    formatSummaryValue(summaryType: string, summaryValue: number, minNumber: number, maxNumber: number): void {
        if (summaryValue !== null && summaryValue !== undefined && !isNaN(summaryValue)) {
            const formattedSummaryValue = new Intl.NumberFormat('de-DE', {
                minimumFractionDigits: minNumber,
                maximumFractionDigits: maxNumber
            }).format(summaryValue);

            this.form.get(summaryType)?.setValue(isNaN(formattedSummaryValue) ? 0 : formattedSummaryValue);
        }
    }

    formatDate(d): any {
        return formatDate(d);
    }

    isPensionType(): boolean {
        return this.data.ruling.isPensionType === 'J' || (this.data.ruling.isChildrenType === 'J' && this.data.ruling.paymentTransactionTypeAcronym.includes('E'))
            || (this.data.ruling.paymentTransactionTypeAcronym.includes('EING') && this.data.ruling.fundPartnerSSN === this.data.ruling.receiverSSN)
            || this.data.ruling.paymentTransactionTypeAcronym === 'B';
    }

    isSpouseType(): boolean {
        return this.data.ruling.isSpouseType === 'J' || (this.data.ruling.isChildrenType === 'J' && this.data.ruling.paymentTransactionTypeAcronym.includes('M'))
            || (this.data.ruling.paymentTransactionTypeAcronym.includes('EING') && this.data.ruling.fundPartnerSSN !== this.data.ruling.receiverSSN);
    }

    isDisablityType(): boolean {
        return this.data.ruling.isDisabilityType === 'J' || (this.data.ruling.isChildrenType === 'J' && this.data.ruling.paymentTransactionTypeAcronym.includes('O'));
    }

    isChildrenType(): boolean {
        return this.data.ruling.isChildrenType === 'J';
    }

    //#endregion
}
